import React, { useState, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import * as firestoreService from '../../services/firestoreService';
import SecurityUtils from '../../utils/security';
import { Send } from 'lucide-react';

const SUBJECTS = [
  'General Question',
  'Bug Report',
  'Account Issue',
  'Feature Request',
  'Feedback',
  'Other',
];

const ContactView = () => {
  const { currentUser, conversations, setView, setMessagesKey } = useContext(AppContext);
  const [subject, setSubject] = useState(SUBJECTS[0]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!message.trim()) { setError('Please enter a message.'); return; }
    setSending(true);
    setError(null);
    try {
      const superAdmin = await firestoreService.getSuperAdmin();
      if (!superAdmin) throw new Error('Could not find support contact. Please try again later.');

      // Find existing DM with the superadmin (not a group chat)
      let conversation = conversations.find(c =>
        !c.isGroup &&
        c.participants.length === 2 &&
        c.participants.includes(currentUser.id) &&
        c.participants.includes(superAdmin.id)
      );

      if (!conversation) {
        conversation = await firestoreService.createConversation({
          participants: [currentUser.id, superAdmin.id],
          lastMessage: '',
          lastMessageTime: new Date().toISOString(),
          readBy: [currentUser.id],
        });
      }

      const sanitized = SecurityUtils.sanitizeText(message.trim());
      await firestoreService.sendMessage({
        conversationId: conversation.id,
        senderId: currentUser.id,
        content: `📋 ${subject}\n\n${sanitized}`,
      });

      setMessagesKey(k => k + 1);
      setView('messages');
    } catch (err) {
      setError(err.message || 'Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto p-4">
      <h2 className="text-xl font-bold text-white mb-1">Contact Us</h2>
      <p className="text-gray-400 text-sm mb-6">Send us a message and we'll get back to you in your inbox.</p>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-white mb-1">Subject</label>
          <select
            value={subject}
            onChange={e => setSubject(e.target.value)}
            className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-[#22c55e]"
          >
            {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-1">Message</label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={6}
            placeholder="Describe your question or issue..."
            className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-[#22c55e] resize-none"
          />
        </div>

        {error && <p className="text-red-500 text-sm">{error}</p>}

        <button
          onClick={handleSubmit}
          disabled={sending}
          className="w-full bg-[#16a34a] text-pure-white py-2 rounded-lg font-medium hover:bg-[#15803d] disabled:opacity-50 flex items-center justify-center space-x-2"
        >
          <Send className="w-4 h-4" />
          <span>{sending ? 'Sending...' : 'Send Message'}</span>
        </button>
      </div>
    </div>
  );
};

export default ContactView;
